import { ComponentType } from '@angular/cdk/overlay';
import { inject } from '@angular/core';
import { CanActivateFn, Routes } from '@angular/router';
import { ApolloClient, InMemoryCache } from '@apollo/client';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { provideApollo } from 'apollo-angular';
import { HttpLink, HttpLinkHandler } from 'apollo-angular/http';
import { DeightonRoleProvider, translocoConf } from '@deighton/shared-ui/services';
import { environment } from '../environments/environment';
import { DashboardComponent } from '../pages/dashboard/dashboard.component';
import { IndividualLicenseComponent } from '../pages/individual-license/individual-license.component';
import { OrganizationLicenseComponent } from '../pages/organization-license/organization-license.component';
import { LicensePayloadBuilderService } from '../pages/organization-license/services/license-payload-builder.service';
import { DialogDataMapperService } from '../pages/organization-license/services/organization-license-dialog.service';
import { PlansComponent } from '../pages/plans/plans.component';
import type { RolesSplitPanelComponent } from '../pages/roles/roles-split-panel/roles-split-panel.component';
import { createInlineLoader } from '../providers/translations';
import { FeatureService } from '../services/feature/feature.service';
import { IndividualLicenseService } from '../services/individual/individual-license.service';
import { OrganizationLicenseService } from '../services/organization/organization-license.service';
import { PlanService } from '../services/plan/plan.service';
import { RoleAssignmentService } from '../services/role/role-assignment.service';
import { RoleBulkSelectionDataSource } from '../services/role/role-bulk-selection.datasource';
import { RoleService } from '../services/role/role.service';
import { TenantService } from '../services/tenant/tenant.service';
import { LicensingRole } from '../shared/constants/licensing-role.enum';
import { AuthCallbackComponent } from './auth-callback/auth-callback.component';
import { authGuard } from './auth.guard';
import { AppLayoutComponent } from './layout/app-layout/app-layout.component';
import { LoginComponent } from './login.component';
import { roleGuard } from './role.guard';

/**
 * Builds the canActivate guards array.
 * When running in standalone mode (environment.okta is set), the auth guard protects routes.
 * When hosted in the shell-app (no environment.okta), no guard is needed.
 */
function getGuards(): Array<CanActivateFn> {
  return environment.okta ? [authGuard] : [];
}

/**
 * Application route configuration.
 * Defines the main and child routes for the licensing application.
 *
 * In standalone mode, authentication is handled by the backend-mediated Okta flow
 * via LicensingAuthService. The /login route triggers the backend redirect and
 * /auth-callback handles the return relay token exchange.
 *
 * When hosted in the shell-app, authentication is managed by the shell.
 */
export const routes: Routes = [
  // Login route (public, no auth required)
  {
    path: 'login',
    component: LoginComponent
  },

  // Auth callback route — handles the relay token returned by the backend auth page.
  {
    path: 'auth-callback',
    component: AuthCallbackComponent,
    providers: [
      {
        provide: TRANSLOCO_SCOPE,
        useValue: {
          scope: 'licensing',
          loader: createInlineLoader(translocoConf.availableLangs as Array<string>)
        }
      }
    ]
  },

  // Main application route (protected when running in standalone mode)
  {
    path: '',
    canActivate: getGuards(),
    component: AppLayoutComponent,
    providers: [
      provideApollo((): ApolloClient.Options => {
        const httpLink: HttpLink = inject(HttpLink);

        // Auth headers (Authorization + X-Deighton-User-Context) are attached by the
        // global `authInterceptor`, which runs for every HttpClient request including
        // Apollo's HttpLink. No Apollo-level auth link is needed here.
        const tenantsLink: HttpLinkHandler = httpLink.create({
          uri: `${environment.platformApiBaseUrl}/tenants/graphql`
        });

        return {
          link: tenantsLink,
          cache: new InMemoryCache(),
          defaultOptions: {
            watchQuery: {
              fetchPolicy: 'cache-and-network',
              errorPolicy: 'all',
              notifyOnNetworkStatusChange: false
            },
            query: {
              fetchPolicy: 'network-only',
              errorPolicy: 'all'
            },
            mutate: {
              errorPolicy: 'all'
            }
          }
        };
      }),
      {
        provide: TRANSLOCO_SCOPE,
        useValue: {
          scope: 'licensing',
          loader: createInlineLoader(translocoConf.availableLangs as Array<string>)
        }
      },
      FeatureService,
      DialogDataMapperService,
      LicensePayloadBuilderService,
      IndividualLicenseService,
      OrganizationLicenseService,
      PlanService,
      DeightonRoleProvider,
      RoleService,
      RoleAssignmentService,
      RoleBulkSelectionDataSource,
      TenantService
    ],
    children: [
      {
        path: '',
        loadComponent: (): Promise<ComponentType<DashboardComponent>> =>
          import('../pages/dashboard/dashboard.component').then(
            (m): ComponentType<DashboardComponent> => m.DashboardComponent
          )
      },
      {
        path: 'licensing/individual',
        loadComponent: (): Promise<ComponentType<IndividualLicenseComponent>> =>
          import('../pages/individual-license/individual-license.component').then(
            (m): ComponentType<IndividualLicenseComponent> => m.IndividualLicenseComponent
          )
      },
      {
        path: 'licensing/organization',
        canActivate: [roleGuard],
        data: { minimumRole: LicensingRole.TenantAdmin },
        loadComponent: (): Promise<ComponentType<OrganizationLicenseComponent>> =>
          import('../pages/organization-license/organization-license.component').then(
            (m): ComponentType<OrganizationLicenseComponent> => m.OrganizationLicenseComponent
          )
      },
      {
        path: 'licensing/organisation',
        redirectTo: 'licensing/organization',
        pathMatch: 'full'
      },
      {
        path: 'roles',
        redirectTo: 'roles/user',
        pathMatch: 'full'
      },
      {
        path: 'roles/split-panel',
        redirectTo: 'roles/user',
        pathMatch: 'full'
      },
      {
        path: 'roles/user',
        canActivate: [roleGuard],
        data: { minimumRole: LicensingRole.User },
        loadComponent: (): Promise<ComponentType<RolesSplitPanelComponent>> =>
          import('../pages/roles/roles-split-panel/roles-split-panel.component').then(
            (m): ComponentType<RolesSplitPanelComponent> => m.RolesSplitPanelComponent
          )
      },
      {
        path: 'roles/user/:id',
        canActivate: [roleGuard],
        data: { minimumRole: LicensingRole.User },
        loadComponent: (): Promise<ComponentType<RolesSplitPanelComponent>> =>
          import('../pages/roles/roles-split-panel/roles-split-panel.component').then(
            (m): ComponentType<RolesSplitPanelComponent> => m.RolesSplitPanelComponent
          )
      },
      {
        path: 'licensing/roles',
        redirectTo: 'roles/user',
        pathMatch: 'full'
      },
      {
        path: 'licensing/roles/user',
        redirectTo: 'roles/user',
        pathMatch: 'full'
      },
      {
        path: 'licensing/plans',
        canActivate: [roleGuard],
        data: { minimumRole: LicensingRole.TenantAdmin },
        loadComponent: (): Promise<ComponentType<PlansComponent>> =>
          import('../pages/plans/plans.component').then(
            (m): ComponentType<PlansComponent> => m.PlansComponent
          )
      }
    ]
  },

  // Redirect unknown routes to home
  { path: '**', redirectTo: '' }
];
